// CategoryPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from "../Api.js";
import { getAuthenticatedSessionToken, getUserSessionToken } from '../utils/auth';
import './CategoryPage.css';

const getRelatedId = (value) => (
  typeof value === 'string' ? value : value?._id
);
const EMPTY_LIST = [];

const CategoryPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const [selectedChildCategory, setSelectedChildCategory] = useState(null);
  const [services, setServices] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [techniciansLoading, setTechniciansLoading] = useState(false);
  const [categoryData, setCategoryData] = useState(() => location.state || {});
  const [categoryContextLoading, setCategoryContextLoading] = useState(!location.state?.category);
  const [categoryContextError, setCategoryContextError] = useState(null);
  
  const category = categoryData.category;
  const subCategory = categoryData.subCategory;
  const childCategories = categoryData.childCategories || EMPTY_LIST;
  const subChildCategories = categoryData.subChildCategories || EMPTY_LIST;

  useEffect(() => {
    if (location.state?.category && location.state?.subCategory) {
      setCategoryData(location.state);
      setCategoryContextError(null);
      setCategoryContextLoading(false);
      return undefined;
    }

    let cancelled = false;
    const loadCategoryContext = async () => {
      setCategoryContextLoading(true);
      setCategoryContextError(null);
      try {
        const [categoriesResponse, subCategoriesResponse, childCategoriesResponse, subChildCategoriesResponse] = await Promise.all([
          api.get('/api/web/categories'),
          api.get('/api/web/sub-categories'),
          api.get('/api/web/child-categories'),
          api.get('/api/web/sub-child-categories')
        ]);
        const categories = categoriesResponse.data.data;
        const subCategories = subCategoriesResponse.data.data;
        const allChildCategories = childCategoriesResponse.data.data;
        const allSubChildCategories = subChildCategoriesResponse.data.data;
        if (![categories, subCategories, allChildCategories, allSubChildCategories].every(Array.isArray)) {
          throw new Error('Category data response is invalid.');
        }

        const currentCategory = categories.find((item) => getRelatedId(item._id) === categoryId && item.status === true);
        const currentSubCategory = subCategories.find((item) => (
          getRelatedId(item.categoryId) === categoryId && item.status === true
        ));
        if (!currentCategory || !currentSubCategory) {
          throw new Error('This category is unavailable.');
        }

        const currentChildCategories = allChildCategories.filter((item) => (
          getRelatedId(item.subCategoryId) === getRelatedId(currentSubCategory._id) && item.status === true
        ));
        const childIds = new Set(currentChildCategories.map((item) => getRelatedId(item._id)));
        const currentSubChildCategories = allSubChildCategories.filter((item) => (
          childIds.has(getRelatedId(item.childCategoryId)) && item.status === true
        ));

        if (!cancelled) {
          setCategoryData({
            category: currentCategory,
            subCategory: currentSubCategory,
            childCategories: currentChildCategories,
            subChildCategories: currentSubChildCategories
          });
          setCategoryContextLoading(false);
        }
      } catch (error) {
        console.error('Error loading category page:', error);
        if (!cancelled) {
          setCategoryContextError('Unable to load this category. Please try again from the categories page.');
          setCategoryContextLoading(false);
        }
      }
    };
    loadCategoryContext();
    return () => {
      cancelled = true;
    };
  }, [categoryId, location.state]);

  const fetchServicesByChildCategory = useCallback(async (childCategoryId) => {
    try {
      setLoading(true);
      const response = await api.get('/api/web/available-services');
      const allServices = response.data.data.services.filter(s => s.status === true);
      
      // Get sub-child categories for this child category
      const subChilds = subChildCategories.filter(
        subChild => getRelatedId(subChild.childCategoryId) === childCategoryId
      );
      
      // Get services for these sub-child categories
      const subChildIds = subChilds.map(sc => sc._id);
      const filteredServices = allServices.filter(
        service => subChildIds.includes(getRelatedId(service.subchildCategoryId))
      );
      
      setServices(filteredServices);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  }, [subChildCategories]);

  const fetchTechniciansByChildCategory = useCallback(async (childCategoryId) => {
    try {
      setTechniciansLoading(true);
      const response = await api.get(`/api/web/technicians-by-child/${childCategoryId}`);
      
      if (response.data.success) {
        // Filter only available technicians
        const availableTechnicians = response.data.data.filter(
          tech => tech.availability_status === 'Available' && tech.status === 'Active'
        );
        setTechnicians(availableTechnicians);
      }
    } catch (error) {
      console.error('Error fetching technicians:', error);
      setTechnicians([]);
    } finally {
      setTechniciansLoading(false);
    }
  }, []);

  useEffect(() => {
    setSelectedChildCategory(childCategories[0] || null);
  }, [childCategories]);

  useEffect(() => {
    if (!selectedChildCategory) {
      setServices([]);
      setTechnicians([]);
      return;
    }

    fetchServicesByChildCategory(selectedChildCategory._id);
    fetchTechniciansByChildCategory(selectedChildCategory._id);
  }, [selectedChildCategory, fetchServicesByChildCategory, fetchTechniciansByChildCategory]);

  // Get sub-child categories for a child category
  const getSubChildCategories = (childCategoryId) => {
    return subChildCategories.filter(
      subChild => getRelatedId(subChild.childCategoryId) === childCategoryId
    );
  };

  // Handle call now button click
  const handleCallNow = async (technician) => {
      try {
    const token = getUserSessionToken();

    if (!token) {
      navigate("/login?tab=user");
      return;
    }

    const response = await api.post(
      "/api/web/technicians/view-create-log",
      {
        technicianId: technician.technician_id,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data.success) {
      window.location.href = `tel:${technician.mobile}`;
    } else {
      alert(
        "You are not authorized to view this profile. Please log in as a user."
      );
    }
  } catch (error) {
    console.error("Profile view error:", error);

    if (error.response?.status === 401) {
      navigate("/login?tab=user");
    } else {
      alert(
        error.response?.data?.message || "Something went wrong. Please try again."
      );
    }
  }
  };

  // Handle view profile button click
  const handleViewProfile = async (technicianId) => {
  try {
    const token = getUserSessionToken();

    if (!token) {
      if (getAuthenticatedSessionToken()) {
        navigate(`/tech-profile/${technicianId}`);
        return;
      }
      navigate("/login?tab=user");
      return;
    }

    const response = await api.post(
      "/api/web/technicians/view-create-log",
      {
        technicianId: technicianId,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data.success) {
      navigate(`/tech-profile/${technicianId}`);
    } else {
      alert(
        "You are not authorized to view this profile. Please log in as a user."
      );
    }
  } catch (error) {
    console.error("Profile view error:", error);

    if (error.response?.status === 401) {
      navigate("/login?tab=user");
    } else {
      alert(
        error.response?.data?.message || "Something went wrong. Please try again."
      );
    }
  }
  };

  if (categoryContextLoading) {
    return <div className="category-page-loading" role="status">Loading category…</div>;
  }

  if (categoryContextError || !category || !subCategory) {
    return <div className="category-page-error">{categoryContextError || 'Category not found'}</div>;
  }

  return (
    <div className="category-page">
      <div className="category-page-header">
        <h1>{category.categoryName}</h1>
        <p>{subCategory.subCategoryName}</p>
      </div>

      <div className="category-page-container">
        {/* Left Side - Child Categories */}
        <div className="category-sidebar">
          <h3>Services</h3>
          <div className="child-category-list">
            {childCategories && childCategories.length > 0 ? (
              childCategories.map((child) => (
                <button
                  type="button"
                  key={child._id}
                  className={`child-category-item ${selectedChildCategory?._id === child._id ? 'active' : ''}`}
                  aria-pressed={selectedChildCategory?._id === child._id}
                  onClick={() => {
                    setSelectedChildCategory(child);
                  }}
                >
                  {child.childCategorylogo && (
                    <img 
                      src={child.childCategorylogo} 
                      alt={child.childCategoryName}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                  <span>{child.childCategoryName}</span>
                  <span className="child-badge">
                    {getSubChildCategories(child._id).length}
                  </span>
                </button>
              ))
            ) : (
              <p className="no-child-categories">No services available</p>
            )}
          </div>
        </div>

        {/* Right Side - Content */}
        <div className="category-content">
          {selectedChildCategory ? (
            <>
              <h2>{selectedChildCategory.childCategoryName}</h2>
              
              {/* Technicians Section */}
              <div className="technicians-section">
                <div className="section-header">
                  <h3>Available Technicians</h3>
                  <span className="technician-count">{technicians.length} technicians</span>
                </div>
                
                {techniciansLoading ? (
                  <div className="technicians-loader">Loading technicians...</div>
                ) : technicians.length > 0 ? (
                  <div className="technicians-grid">
                    {technicians.map((technician) => (
                      <div key={technician._id} className="technician-card">
                        <div className="technician-avatar">
                          {technician.profile_pic ? (
                            <img 
                              src={technician.profile_pic} 
                              alt={technician.name}
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          ) : (
                            <div className="avatar-placeholder">
                              {technician.name.charAt(0)}
                            </div>
                          )}
                          <span className={`availability-badge ${technician.availability_status.toLowerCase()}`}>
                            {technician.availability_status}
                          </span>
                        </div>
                        
                        <div className="technician-info">
                          <h4>{technician.name}</h4>
                          <p className="technician-location">
                            <i className="location-icon">📍</i> 
                            {technician.city}, {technician.zip_code}
                          </p>
                          <p className="technician-address">{technician.address}</p>
                          {/* <div className="technician-details">
                            <span>Blood: {technician.blood_group}</span>
                            <span className={`kyc-status ${technician.kyc_status.toLowerCase()}`}>
                              KYC: {technician.kyc_status}
                            </span>
                            <span className={`subscription-status ${technician.subscription_status.toLowerCase()}`}>
                              {technician.subscription_status}
                            </span>
                          </div> */}
                          <p className="technician-email">
                            <i className="email-icon">✉️</i> {technician.email}
                          </p>
                        </div>
                        
                        <div className="technician-actions">
                          <button 
                            className="call-now-btn"
                            onClick={() => handleCallNow(technician)}
                          >
                            <i className="phone-icon">📞</i> Call Now
                          </button>
                          <button 
                            className="view-profile-btn"
                            onClick={() => handleViewProfile(technician.technician_id)}
                          >
                            <i className="profile-icon">👤</i> View Profile
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-technicians-msg">
                    <p>No technicians available for this service</p>
                  </div>
                )}
              </div>

              <section className="category-services-section" aria-labelledby="category-services-title">
                <div className="section-header">
                  <h3 id="category-services-title">Available services</h3>
                  <span className="technician-count">{services.length} services</span>
                </div>
                {loading ? (
                  <p className="category-services-message" role="status">Loading services…</p>
                ) : services.length > 0 ? (
                  <div className="category-services-grid">
                    {services.map((service) => (
                      <article className="category-service-card" key={service._id}>
                        {service.availableserviceLogo && (
                          <img
                            src={service.availableserviceLogo}
                            alt=""
                            onError={(event) => { event.currentTarget.style.display = 'none'; }}
                          />
                        )}
                        <div className="category-service-card__body">
                          <h4>{service.availableserviceTitle}</h4>
                          {(service.availablestartPrice || service.availableserviceDuration) && (
                            <p>
                              {service.availablestartPrice ? `From $${service.availablestartPrice}` : ''}
                              {service.availablestartPrice && service.availableserviceDuration ? ' · ' : ''}
                              {service.availableserviceDuration || ''}
                            </p>
                          )}
                          <button
                            type="button"
                            onClick={() => navigate(`/services/${service._id}`)}
                          >
                            View service
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="category-services-message">No services are listed for this category yet.</p>
                )}
              </section>
            </>
          ) : (
            <div className="select-prompt">
              <p>Select a service from the left to see available options</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;